import * as React from 'react';
import * as DateFns from 'date-fns';
import $ from 'jquery';
import '../css/calendar.sass';
import CalendarWeekHeader from './CalendarWeekHeader';
import CalendarHeader from './CalendarHeader';
import CalendarDay from './CalendarDay';
import focusTimeSlots from '../utils/focusAppointmentSlots';

interface CalendarProps {
  minDate?: Date | string;
  maxDate?: Date | string;
  unAvailableDates?: Date[];
  disablePastDates?: boolean;
  disableFutureDates?: boolean;
  priorityDate?: Date;
  selectedDate?: Date | string;
  value?: Date | string;
  language?: 'enUS' | 'es';
  onSelect?: Function;
  onChange?: Function;
  onBlur?: Function;
  children?: JSX.Element;
  isForAppointment?: boolean;
  id?: string;
  standalone?: boolean;
}

interface CalendarState {
  currentDate: Date;
  selectedDate?: Date;
  activeDate?: Date;
}

export default function Calendar(props: CalendarProps) {
  let selectedDate = new Date();
  const selected = props.selectedDate || props.value;
  if (selected) {
    selectedDate = selected instanceof Date ? selected : new Date(selected);
  }

  const [state, setState] = React.useState<CalendarState>({
    currentDate: selected ? selectedDate : new Date(),
    selectedDate: selected ? selectedDate : new Date(),
    activeDate: selected ? selectedDate : new Date()
  });

  let minDateValue;
  let maxDateValue;

  if (props.minDate) {
    minDateValue =
      props.minDate instanceof Date ? props.minDate : new Date(props.minDate);
  }

  if (props.maxDate) {
    maxDateValue =
      props.maxDate instanceof Date ? props.maxDate : new Date(props.maxDate);
  }

  const minDate = props.disablePastDates
    ? new Date(new Date().toDateString())
    : minDateValue;

  const maxDate = props.disableFutureDates
    ? new Date(new Date().toDateString())
    : maxDateValue;

  const calendarDiv: any = React.useRef();

  const handleClickOutside = (event: any) => {
    if (calendarDiv.current.contains(event.target)) {
      return;
    }
    if (props.onBlur !== undefined) {
      props.onBlur();
    }
  };

  const handleHideDrawerOnEsc = (e: any) => {
    if (e.key === 'Escape' && props.onBlur !== undefined) {
      props.onBlur();
    }
  };

  const resizeCalendar = () => {
    $('.keyboard').css('width', $('.keyboard').prev().css('width'));
  };

  React.useEffect(() => {
    resizeCalendar();
  });

  window.addEventListener('resize', () => {
    resizeCalendar();
  });

  let prevMonthBtn: any;
  let nextMonthBtn: any;
  let activeDayElem: any;
  if (calendarDiv.current) {
    prevMonthBtn = calendarDiv.current.querySelector('.arrow-left');
    nextMonthBtn = calendarDiv.current.querySelector('.arrow-right');
    activeDayElem = calendarDiv.current.querySelector('.day.active');
  }

  React.useEffect(() => {
    if (calendarDiv.current) {
      prevMonthBtn = calendarDiv.current.querySelector('.arrow-left');
      nextMonthBtn = calendarDiv.current.querySelector('.arrow-right');
      activeDayElem = calendarDiv.current.querySelector('.day.active');
    }

    document.addEventListener('keydown', handleHideDrawerOnEsc);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleHideDrawerOnEsc);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [props]);

  let unAvailableDates = props.unAvailableDates || [];

  const listContainsDate = (list: Date[], date: Date): boolean => {
    return list.some((d) => d.toDateString() === date.toDateString());
  };

  const nextAvailableDate = (date: Date) => {
    let returnDate: Date = DateFns.addDays(date, 1);
    while (listContainsDate(unAvailableDates, returnDate)) {
      returnDate = DateFns.addDays(returnDate, 1);
    }
    return returnDate;
  };

  const pastAvailableDate = (date: Date) => {
    let returnDate: Date = DateFns.subDays(date, 1);
    while (listContainsDate(unAvailableDates, returnDate)) {
      returnDate = DateFns.subDays(returnDate, 1);
    }
    return returnDate;
  };

  const nextMonth = () => {
    setState({
      ...state,
      activeDate:
        state.activeDate &&
        nextAvailableDate(DateFns.endOfMonth(state.currentDate)),
      currentDate: DateFns.addMonths(state.currentDate, 1)
    });
  };

  const prevMonth = () => {
    setState({
      ...state,
      activeDate:
        state.activeDate &&
        pastAvailableDate(DateFns.startOfMonth(state.currentDate)),
      currentDate: DateFns.subMonths(state.currentDate, 1)
    });
  };

  const cells = () => {
    const monthStart = DateFns.startOfMonth(state.currentDate);
    const monthEnd = DateFns.endOfMonth(monthStart);
    const startDate: Date = DateFns.startOfWeek(monthStart);
    const endDate = DateFns.endOfWeek(monthEnd);
    const rows: any = [];
    const dates: any = [];

    if (maxDate) {
      if (DateFns.isSameMonth(DateFns.addDays(maxDate, 1), monthEnd)) {
        unAvailableDates = unAvailableDates.concat(
          DateFns.eachDayOfInterval({
            start: DateFns.addDays(maxDate, 1),
            end: DateFns.endOfMonth(monthEnd)
          })
        );
      } else if (DateFns.isBefore(maxDate, monthStart)) {
        unAvailableDates = unAvailableDates.concat(
          DateFns.eachDayOfInterval({
            start: monthStart,
            end: monthEnd
          })
        );
      }
    }

    if (minDate) {
      if (DateFns.isSameMonth(DateFns.subDays(minDate, 1), monthStart)) {
        unAvailableDates = unAvailableDates.concat(
          DateFns.eachDayOfInterval({
            start: DateFns.startOfMonth(monthStart),
            end: DateFns.subDays(minDate, 1)
          })
        );
      } else if (DateFns.isAfter(minDate, monthEnd)) {
        unAvailableDates = unAvailableDates.concat(
          DateFns.eachDayOfInterval({
            start: DateFns.startOfMonth(monthStart),
            end: monthEnd
          })
        );
      }
    }

    let days: any = [];
    let day: Date = startDate;

    let index = 0;
    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        if (!DateFns.isSameMonth(day, monthStart)) {
          days.push(
            <CalendarDay key={day.toDateString()} day={day} state="off" />
          );
        } else if (listContainsDate(unAvailableDates, day)) {
          days.push(
            <CalendarDay
              key={day.toDateString()}
              day={day}
              state="unavailable"
            />
          );
        } else {
          let calendarDayState:
            | 'off'
            | 'unavailable'
            | 'selected'
            | 'priority'
            | 'available' = 'available';
          if (
            props.priorityDate &&
            props.priorityDate.toDateString() === day.toDateString()
          ) {
            calendarDayState = 'priority';
          } else if (
            state.selectedDate &&
            state.selectedDate.toDateString() === day.toDateString()
          ) {
            calendarDayState = 'selected';
          }
          dates[index] = day;
          days.push(
            <CalendarDay
              key={day.toDateString()}
              language={props.language}
              focus={
                state.activeDate &&
                (listContainsDate(unAvailableDates, state.activeDate)
                  ? nextAvailableDate(state.activeDate).toDateString() ===
                    day.toDateString()
                  : state.activeDate.toDateString() === day.toDateString())
              }
              index={index}
              onFocus={(buttonIndex: number, selectedDay: Date) => {
                if (buttonIndex === 0) {
                  setState({ ...state, activeDate: selectedDay });
                }
              }}
              onSelect={(selectedDay: Date) => {
                setState({ ...state, selectedDate: selectedDay });
                if (props.onSelect !== undefined) {
                  props.onSelect(selectedDay);
                }
                if (props.onChange) {
                  props.onChange({
                    target: {
                      value: DateFns.format(selectedDay, 'MM/dd/yyyy'),
                      valid: true
                    }
                  });
                }
              }}
              onNextDay={(buttonIndex: number) => {
                if (dates.length > buttonIndex + 1) {
                  setState({ ...state, activeDate: dates[buttonIndex + 1] });
                } else if (
                  !(
                    maxDate &&
                    maxDate.getMonth() === state.currentDate.getMonth()
                  )
                ) {
                  nextMonth();
                }
              }}
              onPrevDay={(buttonIndex: number) => {
                if (buttonIndex > 0) {
                  setState({ ...state, activeDate: dates[buttonIndex - 1] });
                } else if (
                  !(
                    minDate &&
                    minDate.getMonth() === state.currentDate.getMonth()
                  )
                ) {
                  prevMonth();
                }
              }}
              onTab={(selectedDay: Date) => {
                if (props.isForAppointment) {
                  focusTimeSlots();
                } else if (prevMonthBtn) {
                  prevMonthBtn.focus();
                } else if (nextMonthBtn) {
                  nextMonthBtn.focus();
                } else {
                  setState({ ...state, selectedDate: selectedDay });
                  if (props.onSelect !== undefined) {
                    props.onSelect(selectedDay);
                  }
                }
              }}
              onShiftTab={(selectedDay: Date) => {
                if (nextMonthBtn) {
                  nextMonthBtn.focus();
                } else if (prevMonthBtn) {
                  prevMonthBtn.focus();
                } else {
                  setState({ ...state, selectedDate: selectedDay });
                  if (props.onSelect !== undefined) {
                    props.onSelect(selectedDay);
                  }
                }
              }}
              tabIndex={index === 0 ? undefined : -1}
              day={day}
              state={calendarDayState}
            />
          );
          index += 1;
        }
        day = DateFns.addDays(day, 1);
      }
      rows.push(
        <div key={`week-${day.toDateString()}`} className="week">
          {days}
        </div>
      );
      days = [];
    }
    return rows;
  };

  React.useEffect(() => {
   if(!props.value && !props.selectedDate){
    if(props.onChange){
      props.onChange({
        target: {
          value: DateFns.format(selectedDate, 'MM/dd/yyyy'),
          valid: true
        }
      });
    }
   }
  }, []);

  return (
    <div
      ref={calendarDiv}
      aria-modal="true"
      className={`date-picker keyboard${
        props.standalone ? ' standalone-calendar' : ''
      }`}
      id={props.id}
    >
      <CalendarHeader
        currentDate={state.currentDate}
        language={props.language}
        isForAppointment={props.isForAppointment}
        maxDate={maxDate}
        minDate={minDate}
        activeDayElem={activeDayElem}
        onNextMonth={nextMonth}
        onPrevMonth={prevMonth}
      />
      <CalendarWeekHeader language={props.language} />
      {cells()}
      <div className="calendar-notes">{props.children}</div>
    </div>
  );
}
